import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Button from '../../components/Common/Button';
import FileUpload from '../../components/Common/FileUpload';
import MultiFileUpload from '../../components/Common/MultiFileUpload';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import api from '../../utils/api';
import { formatPrice, formatArea, formatStatus } from '../../utils/formatters';
import { showSuccess, showError, showDeleteConfirm } from '../../utils/swal';
import {
  ArrowLeft,
  Edit,
  MapPin,
  Building2,
  DollarSign,
  Plus,
  Trash2,
  FileText,
  Image as ImageIcon,
  MapPinned,
  Save,
  X
} from 'lucide-react';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [editingBlock, setEditingBlock] = useState(null);
  const [blockForm, setBlockForm] = useState({ name: '', sold_out: false });
  const [uploadingFloorPlan, setUploadingFloorPlan] = useState(false);
  const [uploadingSitePlan, setUploadingSitePlan] = useState(false);

  // Inline editing states
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await api.get(`/projects/${id}`);
      setProject(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Proje yüklenemedi:', error);
      setLoading(false);
    }
  };

  // Info editing handlers
  const handleEditInfo = () => {
    setInfoForm({
      min_sqm: project.min_sqm || '',
      max_sqm: project.max_sqm || '',
      min_price: project.min_price || '',
      max_price: project.max_price || '',
      min_room_count: project.min_room_count || '',
      extra_info: project.extra_info || ''
    });
    setEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    try {
      await api.put(`/projects/${id}`, infoForm);
      await fetchProject();
      setEditingInfo(false);
    } catch (error) {
      console.error('Proje bilgileri güncellenemedi:', error);
      showError('Proje bilgileri güncellenirken bir hata oluştu', 'Güncelleme Hatası');
    }
  };

  const handleFloorPlanUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // 1. Upload the file
      const uploadResp = await api.post(
        `/projects/${id}/upload-floor-plan`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (!uploadResp.data.success) {
        throw new Error(uploadResp.data.message || "Yükleme başarısız");
      }

      const newPath = uploadResp.data.files[0].path;
      const newUrl = uploadResp.data.files[0].url;

      // 2. Get the latest project state to avoid race conditions in sequential uploads
      const latestResp = await api.get(`/projects/${id}`);
      const latestPlans = latestResp.data.floor_plan || [];

      // 3. Update the project with the new path appended
      await api.put(`/projects/${id}`, {
        floor_plan: [...latestPlans, newPath],
      });

      fetchProject();
      return { url: newUrl, path: newPath };
    } catch (error) {
      console.error("Floor plan upload error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors ||
        error.message ||
        "Kat planı yüklenirken bir hata oluştu";
      throw new Error(errorMessage);
    }
  };

  const handleSitePlanUpload = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // 1. Upload the file
      const uploadResp = await api.post(
        `/projects/${id}/upload-site-plan`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (!uploadResp.data.success) {
        throw new Error(uploadResp.data.message || "Yükleme başarısız");
      }

      const newPath = uploadResp.data.files[0].path;
      const newUrl = uploadResp.data.files[0].url;

      // 2. Get the latest project state to avoid race conditions
      const latestResp = await api.get(`/projects/${id}`);
      const latestPlans = latestResp.data.site_plan || [];

      // 3. Update the project record
      await api.put(`/projects/${id}`, {
        site_plan: [...latestPlans, newPath],
      });

      fetchProject();
      return { url: newUrl, path: newPath };
    } catch (error) {
      console.error("Site plan upload error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.errors ||
        error.message ||
        "Vaziyet planı yüklenirken bir hata oluştu";
      throw new Error(errorMessage);
    }
  };

  const handleDeleteFile = async (type, index) => {
    const fileName = type === 'floor' ? 'Bu kat planı' : 'Bu vaziyet planı';
    const result = await showDeleteConfirm(fileName);

    if (!result.isConfirmed) {
      return;
    }

    const plans = type === 'floor' ? project.floor_plan : project.site_plan;

    if (!plans || !Array.isArray(plans)) return;

    const path = plans[index];

    try {
      await api.delete('/files', { data: { path } });

      // Remove from array
      const newPlans = plans.filter((_, i) => i !== index);

      await api.put(`/projects/${id}`, {
        ...project,
        [type === 'floor' ? 'floor_plan' : 'site_plan']: newPlans.length > 0 ? newPlans : null
      });
      fetchProject();
      showSuccess('Dosya başarıyla silindi!');
    } catch (error) {
      console.error('Dosya silinemedi:', error);
      showError('Dosya silinirken hata oluştu', 'Silme Hatası');
    }
  };

  const handleAddBlock = () => {
    setEditingBlock(null);
    setBlockForm({ name: '', sold_out: false });
    setShowBlockModal(true);
  };

  const handleEditBlock = (block) => {
    setEditingBlock(block);
    setBlockForm({ name: block.name, sold_out: block.sold_out });
    setShowBlockModal(true);
  };

  const handleSaveBlock = async () => {
    try {
      if (editingBlock) {
        // Update existing block
        await api.put(`/project-blocks/${editingBlock.id}`, blockForm);
      } else {
        // Create new block
        await api.post('/project-blocks', {
          ...blockForm,
          project_id: id
        });
      }
      setShowBlockModal(false);
      fetchProject();
      showSuccess(editingBlock ? 'Blok başarıyla güncellendi!' : 'Blok başarıyla eklendi!');
    } catch (error) {
      console.error('Blok kaydedilemedi:', error);
      showError('Blok kaydedilirken hata oluştu', 'Kaydetme Hatası');
    }
  };

  const handleDeleteBlock = async (blockId) => {
    const result = await showDeleteConfirm('Bu blok');
    if (!result.isConfirmed) {
      return;
    }

    try {
      await api.delete(`/project-blocks/${blockId}`);
      fetchProject();
      showSuccess('Blok başarıyla silindi!');
    } catch (error) {
      console.error('Blok silinemedi:', error);
      showError('Blok silinirken hata oluştu', 'Silme Hatası');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return <div className="text-center py-8">Proje bulunamadı</div>;
  }

  const portfolioColumns = [
    { header: 'Başlık', accessor: 'title' },
    {
      header: "Emlak Tipi",
      accessor: "property_type",
      render: (row) => {
        const code = row.property_type?.code;
        const getPropertyTypeColor = (typeCode) => {
          switch (typeCode) {
            case 'residential': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'land': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'commercial': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPropertyTypeColor(code)}`}>
            {row.property_type?.name || "-"}
          </span>
        );
      },
    },
    { header: 'Oda', render: (row) => row.room_definition?.name || "-" },
    {
      header: 'Metrekare',
      render: (row) => formatArea(row.net_sqm)
    },
    {
      header: 'Fiyat',
      render: (row) => formatPrice(row.list_price)
    },
    {
      header: "Durum",
      accessor: "status",
      render: (row) => {
        const code = row.status?.code;
        const getStatusColor = (statusCode) => {
          switch (statusCode) {
            case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
            case 'deposit': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
            case 'option': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
            case 'manager_option': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
            case 'sold': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
            case 'rented': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
            default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
          }
        };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(code)}`}>
            {row.status?.name || "-"}
          </span>
        );
      },
    },
  ];

  const soldCount = project.portfolios?.filter(p => p.status === 'sold').length || 0;
  const totalCount = project.portfolios?.length || 0;
  const availableCount = totalCount - soldCount;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <Button variant="secondary" size="sm" onClick={() => navigate('/projects')}>
          <ArrowLeft size={16} />
          Geri
        </Button>
        <Button size="sm" onClick={() => navigate(`/projects/${id}/edit`)}>
          <Edit size={16} />
          Düzenle
        </Button>
      </div>

      {/* Header Card */}
      <Card className="mb-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">{project.name}</h1>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <MapPin size={18} className="flex-shrink-0" />
                <span>{project.location}</span>
              </div>

              {project.address && (
                <div className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                  <MapPinned size={18} className="flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{project.address}</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Building2 size={18} className="flex-shrink-0" />
                <span>{project.owner}</span>
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Toplam Değer</div>
            <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
              {formatPrice(project.total_value)}
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="hover:scale-[1.02] transition-transform cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Toplam Gayrimenkul</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalCount}</p>
            </div>
            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg">
              <Building2 size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </Card>

        <Card className="hover:scale-[1.02] transition-transform cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Satışta</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{availableCount}</p>
            </div>
            <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg">
              <DollarSign size={24} className="text-green-600 dark:text-green-400" />
            </div>
          </div>
        </Card>

        <Card className="hover:scale-[1.02] transition-transform cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Satıldı</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{soldCount}</p>
            </div>
            <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-lg">
              <DollarSign size={24} className="text-red-600 dark:text-red-400" />
            </div>
          </div>
        </Card>

        <Card className="hover:scale-[1.02] transition-transform cursor-pointer">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Blok Sayısı</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{project.blocks?.length || 0}</p>
            </div>
            <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
              <Building2 size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Proje Bilgileri */}
        <Card
          title="Proje Bilgileri"
          className="lg:col-span-2"
          action={
            !editingInfo ? (
              <button
                onClick={handleEditInfo}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                title="Düzenle"
              >
                <Edit size={16} />
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditingInfo(false)}
                  className="px-3 py-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                >
                  İptal
                </button>
                <button
                  onClick={handleSaveInfo}
                  className="px-3 py-1.5 bg-primary-600 text-white hover:bg-primary-700 rounded-lg text-sm flex items-center gap-1"
                >
                  <Save size={14} />
                  Kaydet
                </button>
              </div>
            )
          }
        >
          {!editingInfo ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Min Metrekare</p>
                  <p className="font-semibold">{formatArea(project.min_sqm)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Max Metrekare</p>
                  <p className="font-semibold">{formatArea(project.max_sqm)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Min Fiyat</p>
                  <p className="font-semibold">{formatPrice(project.min_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Max Fiyat</p>
                  <p className="font-semibold">{formatPrice(project.max_price)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Min Oda Sayısı</p>
                  <p className="font-semibold">{project.min_room_count || '-'}</p>
                </div>
              </div>

              {project.extra_info && (
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-2">Ek Bilgiler</p>
                  <p className="text-gray-700 whitespace-pre-wrap">{project.extra_info}</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input
                  label="Min Metrekare"
                  type="number"
                  value={infoForm.min_sqm}
                  onChange={(e) => setInfoForm({ ...infoForm, min_sqm: e.target.value })}
                />
                <Input
                  label="Max Metrekare"
                  type="number"
                  value={infoForm.max_sqm}
                  onChange={(e) => setInfoForm({ ...infoForm, max_sqm: e.target.value })}
                />
                <Input
                  label="Min Fiyat"
                  type="number"
                  value={infoForm.min_price}
                  onChange={(e) => setInfoForm({ ...infoForm, min_price: e.target.value })}
                />
                <Input
                  label="Max Fiyat"
                  type="number"
                  value={infoForm.max_price}
                  onChange={(e) => setInfoForm({ ...infoForm, max_price: e.target.value })}
                />
                <Input
                  label="Min Oda Sayısı"
                  type="number"
                  value={infoForm.min_room_count}
                  onChange={(e) => setInfoForm({ ...infoForm, min_room_count: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ek Bilgiler</label>
                <textarea
                  value={infoForm.extra_info}
                  onChange={(e) => setInfoForm({ ...infoForm, extra_info: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                  rows={4}
                  placeholder="Proje hakkında ek bilgiler..."
                />
              </div>
            </div>
          )}
        </Card>

        {/* Bloklar */}
        <Card title="Bloklar">
          <div className="space-y-2 mb-4">
            {project.blocks && project.blocks.length > 0 ? (
              project.blocks.map((block) => (
                <div
                  key={block.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group"
                >
                  <button
                    onClick={() => navigate(`/project-blocks/${block.id}`)}
                    className="flex-1 text-left"
                  >
                    <span className="font-medium">{block.name}</span>
                    <p className="text-xs text-gray-500 mt-1">
                      {block.portfolios_count || 0} gayrimenkul
                    </p>
                  </button>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${block.sold_out
                      ? 'bg-red-100 text-red-800'
                      : 'bg-green-100 text-green-800'
                      }`}>
                      {block.sold_out ? 'Tükendi' : 'Aktif'}
                    </span>
                    <button
                      onClick={() => handleEditBlock(block)}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
                      title="Düzenle"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteBlock(block.id)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg opacity-0 group-hover:opacity-100 transition-all hover:scale-110"
                      title="Sil"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">Blok bilgisi yok</p>
            )}
          </div>

          <Button onClick={handleAddBlock}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]">       <Plus size={18} />
            <span>Yeni Blok Ekle</span></Button>
        </Card>
      </div>

      {/* Planlar */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Kat Planı */}
        <Card title="Kat Planları">
          <MultiFileUpload
            onUpload={handleFloorPlanUpload}
            currentFiles={project.floor_plan_urls ? project.floor_plan_urls.map((url, index) => ({
              url: url,
              name: project.floor_plan[index]?.split('/').pop() || `Dosya ${index + 1}`,
              preview: url
            })) : []}
            onDelete={(index) => handleDeleteFile('floor', index)}
            label=""
            description="WEBP, JPG, PNG veya PDF (Max 10MB, Max 10 dosya)"
            accept="image/*,application/pdf"
            maxFiles={10}
          />
        </Card>

        {/* Vaziyet Planı */}
        <Card title="Vaziyet Planları">
          <MultiFileUpload
            onUpload={handleSitePlanUpload}
            currentFiles={project.site_plan_urls ? project.site_plan_urls.map((url, index) => ({
              url: url,
              name: project.site_plan[index]?.split('/').pop() || `Dosya ${index + 1}`,
              preview: url
            })) : []}
            onDelete={(index) => handleDeleteFile('site', index)}
            label=""
            description="WEBP, JPG, PNG veya PDF (Max 10MB, Max 10 dosya)"
            accept="image/*,application/pdf"
            maxFiles={10}
          />
        </Card>
      </div>

      {/* Gayrimenkuller */}
      <Card title="Projedeki Gayrimenkuller" noPadding>
        {project.portfolios && project.portfolios.length > 0 ? (
          <Table
            columns={portfolioColumns}
            data={project.portfolios}
            onView={(row) => navigate(`/portfolios/${row.id}`)}
            onEdit={(row) => navigate(`/portfolios/${row.id}/edit`)}
            onDelete={(row) => {
              if (window.confirm('Bu gayrimenkulü silmek istediğinizden emin misiniz?')) {
                // Delete logic here
                console.log('Delete portfolio:', row.id);
              }
            }}
          />
        ) : (
          <div className="p-6 text-center text-gray-500">
            Bu projeye ait gayrimenkul bulunmuyor
          </div>
        )}
      </Card>

      {/* Blok Modal */}
      {showBlockModal && (
        <Modal
          isOpen={showBlockModal}
          onClose={() => setShowBlockModal(false)}
          title={editingBlock ? 'Blok Düzenle' : 'Yeni Blok Ekle'}
        >
          <div className="space-y-4">
            <Input
              label="Blok Adı"
              value={blockForm.name}
              onChange={(e) => setBlockForm({ ...blockForm, name: e.target.value })}
              placeholder="Örn: A Blok"
              required
            />

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sold_out"
                checked={blockForm.sold_out}
                onChange={(e) => setBlockForm({ ...blockForm, sold_out: e.target.checked })}
                className="rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="sold_out" className="text-sm text-gray-700 dark:text-gray-300">
                Tükendi olarak işaretle
              </label>
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <button
                onClick={() => setShowBlockModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleSaveBlock}
                className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {editingBlock ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ProjectDetail;
